/**
 * Ops-API client for the routing DR surface (#1421 P3b).
 *
 * Kept apart from `tenantRouting.ts` because these endpoints answer a
 * different operator question — "which config do we fail over to, and is that
 * plan still true?" — and because the switch-event half is append-only history
 * rather than routing-config CRUD.
 */
import type {
  DeleteRoutingDrPlanResponse,
  DisengageRoutingEmergencyOverrideRequest,
  DisengageRoutingEmergencyOverrideResponse,
  EngageRoutingEmergencyOverrideRequest,
  EngageRoutingEmergencyOverrideResponse,
  GetRoutingEmergencyOverrideResponse,
  ListRoutingDrPlansResponse,
  ListRoutingSwitchEventsResponse,
  RollbackRoutingSwitchRequest,
  RollbackRoutingSwitchResponse,
  RoutingConfigPreflightRequest,
  RoutingConfigPreflightResponse,
  UpsertRoutingDrPlanRequest,
  UpsertRoutingDrPlanResponse,
  VerifyRoutingDrPlanRequest,
  VerifyRoutingDrPlanResponse,
} from '@nadoutong/contracts';
import { OpsApiError, opsApiRequest } from './client.ts';

const BASE = '/v1/ops/tenant-routing';

/**
 * How many switch events the panel reads. The gateway clamps its own maximum;
 * this is the console's view size — enough to cover an incident's worth of
 * switching without turning the panel into a history browser.
 */
export const SWITCH_EVENTS_PAGE_SIZE = 20;

export async function listRoutingDrPlans(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingDrPlansResponse> {
  return opsApiRequest<ListRoutingDrPlansResponse>(`${BASE}/dr-plans`, { signal }, fetchImpl);
}

export async function upsertRoutingDrPlan(
  configId: string,
  body: UpsertRoutingDrPlanRequest,
  fetchImpl: typeof fetch,
): Promise<UpsertRoutingDrPlanResponse> {
  const label = body.label.trim();
  if (label.length === 0) {
    // Refused here rather than sent and bounced: an unlabelled plan is
    // indistinguishable from the config it marks, which defeats the list.
    throw new OpsApiError(400, 'INVALID_PAYLOAD', 'a DR plan needs a label', false);
  }
  return opsApiRequest<UpsertRoutingDrPlanResponse>(
    `${BASE}/dr-plans/${encodeURIComponent(configId)}`,
    { method: 'PUT', body: { ...body, label } },
    fetchImpl,
  );
}

export async function deleteRoutingDrPlan(
  configId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRoutingDrPlanResponse> {
  return opsApiRequest<DeleteRoutingDrPlanResponse>(
    `${BASE}/dr-plans/${encodeURIComponent(configId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

/**
 * Read-only preflight. Writes nothing, so it is safe to run repeatedly during
 * an incident — that property is the whole reason it is a separate endpoint
 * from `verify` below, and the console keeps the two actions separate for the
 * same reason.
 */
export async function runRoutingConfigPreflight(
  configId: string,
  body: RoutingConfigPreflightRequest,
  fetchImpl: typeof fetch,
): Promise<RoutingConfigPreflightResponse> {
  return opsApiRequest<RoutingConfigPreflightResponse>(
    `${BASE}/configs/${encodeURIComponent(configId)}/preflight`,
    { method: 'POST', body },
    fetchImpl,
  );
}

/** Runs the same preflight and records its summary on the plan. */
export async function verifyRoutingDrPlan(
  configId: string,
  body: VerifyRoutingDrPlanRequest,
  fetchImpl: typeof fetch,
): Promise<VerifyRoutingDrPlanResponse> {
  return opsApiRequest<VerifyRoutingDrPlanResponse>(
    `${BASE}/dr-plans/${encodeURIComponent(configId)}/verify`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function listRoutingSwitchEvents(
  limit: number,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingSwitchEventsResponse> {
  const qs = new URLSearchParams({ limit: String(limit) });
  return opsApiRequest<ListRoutingSwitchEventsResponse>(
    `${BASE}/switch-events?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

/**
 * A rollback is an ordinary switch, so it carries the same required reason as
 * `setGlobalRoutingConfig` and is refused client-side on an empty one for the
 * same reason: a rollback with no stated cause is the row the next incident
 * review needs most and can read least.
 *
 * `expected_event_id` is required *by this client* even though the wire
 * contract keeps it optional. The endpoint resolves the target itself, so a
 * request without the pin means "roll back whatever is on top now", which is
 * not what any operator read on screen. The optionality exists for older
 * callers; a console that shows a target has no business using it.
 */
export async function rollbackRoutingSwitch(
  body: RollbackRoutingSwitchRequest & { readonly expected_event_id: string },
  fetchImpl: typeof fetch,
): Promise<RollbackRoutingSwitchResponse> {
  const reason = body.reason.trim();
  if (reason.length === 0) {
    throw new OpsApiError(
      400,
      'INVALID_PAYLOAD',
      'a reason is required to roll the global routing config back',
      false,
    );
  }
  if (body.expected_event_id.length === 0) {
    throw new OpsApiError(
      400,
      'INVALID_PAYLOAD',
      'a rollback must name the switch event it reverses',
      false,
    );
  }
  return opsApiRequest<RollbackRoutingSwitchResponse>(
    `${BASE}/switch-events/rollback`,
    { method: 'POST', body: { ...body, reason } },
    fetchImpl,
  );
}

// ── Emergency override (#1422) ──────────────────────────────────────────────

/**
 * Read what the override is currently doing.
 *
 * Note the response distinguishes `engaged` (stored intent) from `effective`
 * (what the request path honours). The panel must key its "the fleet is
 * overridden" state on `effective`, never on `engaged` — they differ once the
 * override passes `expires_at`, and showing an expired override as in force is
 * the one lie this surface must not tell.
 */
export async function getRoutingEmergencyOverride(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetRoutingEmergencyOverrideResponse> {
  return opsApiRequest<GetRoutingEmergencyOverrideResponse>(
    `${BASE}/emergency-override`,
    { signal },
    fetchImpl,
  );
}

/**
 * Engage. Refused client-side without a reason or a preflight token, matching
 * the gateway — which requires the token here even though the ordinary global
 * switch leaves it optional, because this control moves every tenant.
 */
export async function engageRoutingEmergencyOverride(
  body: EngageRoutingEmergencyOverrideRequest,
  fetchImpl: typeof fetch,
): Promise<EngageRoutingEmergencyOverrideResponse> {
  const reason = body.reason.trim();
  if (reason.length === 0) {
    throw new OpsApiError(
      400,
      'INVALID_PAYLOAD',
      'a reason is required to engage the emergency routing override',
      false,
    );
  }
  if (body.preflight_token.trim().length === 0) {
    throw new OpsApiError(
      400,
      'INVALID_PAYLOAD',
      'run the preflight before engaging the emergency routing override',
      false,
    );
  }
  return opsApiRequest<EngageRoutingEmergencyOverrideResponse>(
    `${BASE}/emergency-override/engage`,
    { method: 'POST', body: { ...body, reason } },
    fetchImpl,
  );
}

export async function disengageRoutingEmergencyOverride(
  body: DisengageRoutingEmergencyOverrideRequest,
  fetchImpl: typeof fetch,
): Promise<DisengageRoutingEmergencyOverrideResponse> {
  const reason = body.reason.trim();
  if (reason.length === 0) {
    throw new OpsApiError(
      400,
      'INVALID_PAYLOAD',
      'a reason is required to disengage the emergency routing override',
      false,
    );
  }
  return opsApiRequest<DisengageRoutingEmergencyOverrideResponse>(
    `${BASE}/emergency-override/disengage`,
    { method: 'POST', body: { reason } },
    fetchImpl,
  );
}

/** Suggested expiry offsets, in hours, offered when engaging. */
export const OVERRIDE_EXPIRY_HOURS_OPTIONS = [2, 4, 12, 24] as const;
